import { InvalidVinWarning } from '@/containers/steps/vehicle/InvalidVinWarning'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import {
  Alert,
  Box,
  Button,
  HStack,
  Icon,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack
} from '@chakra-ui/react'
import { faClose } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { InputFormControl, SwitchFormControl } from 'BrokerTsLib/src/components'
import { NumberFormControl } from 'BrokerTsLib/src/components/forms/NumberFormControl'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { type ClientAdditionalInfoDto, type InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { transformVIN } from 'BrokerTsLib/src/Utils'
import { useEffect, useMemo, useState } from 'react'
import { isAfter, isBefore, startOfDay } from 'date-fns'
import * as yup from 'yup'
import { BadgeStep } from './ui/BadgeStep'

interface LeasingDriverInfoDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: ClientAdditionalInfoDto) => Promise<any>
  isSaving?: boolean
  initialValues?: ClientAdditionalInfoDto | null
  required?: boolean
}

export const LeasingDriverInfoDialog = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  initialValues,
  required,
}: LeasingDriverInfoDialogProps) => {
  const { translate } = useTranslationsContext()

  const { insuranceParams: insuranceParamsBase } = useInsuranceParams()
  const insuranceParams = insuranceParamsBase as InsuranceParamsLeasingDto

  const firstRegistrationDate = insuranceParams?.vehicle?.firstRegistrationDate
  const today = startOfDay(new Date())
  const isFirstRegistrationInFuture = !!firstRegistrationDate && !isAfter(today, startOfDay(new Date(firstRegistrationDate)))
  const isFirstRegistrationInPast = !isFirstRegistrationInFuture

  const validationSchema = useMemo(() => yup.object().shape({
    vin: yup.string()
      .nullable()
      .transform((v) => (typeof v === 'string' ? v.trim() : v))
      .when([], {
        is: () => isFirstRegistrationInPast,
        then: (schema) => schema
          .required(translate('leasingDriverInfoDialog.vinRequiredMessage', 'VIN je povinný, pokud je datum první registrace v minulosti'))
          .length(17, translate('leasingDriverInfoDialog.vinLengthMessage', 'VIN musí mít 17 znaků')),
        otherwise: (schema) => schema.test(
          'vin-length',
          translate('leasingDriverInfoDialog.vinLengthMessage', 'VIN musí mít 17 znaků'),
          (value) => !value || value.length === 17,
        ),
      }),
    claims: yup.number()
      .typeError(translate('leasingDriverInfoDialog.claimsRequiredMessage', 'Zadejte počet nehod'))
      .min(0, translate('leasingDriverInfoDialog.claimsMinValueMessage', 'Počet nehod nesmí být záporný'))
      .integer(translate('leasingDriverInfoDialog.claimsIntegerValueMessage', 'Počet nehod musí být celé číslo'))
      .required(translate('leasingDriverInfoDialog.claimsRequiredMessage', 'Zadejte počet nehod')),
    driverYoungerThan24: yup.boolean(),
  }), [isFirstRegistrationInPast])

  const methods = useForm<ClientAdditionalInfoDto>({
    defaultValues: {
      claims: initialValues?.claims ?? 0,
      driverYoungerThan24: initialValues?.driverYoungerThan24 ?? false,
      vin: initialValues?.vin ?? '',
    },
    resolver: yupResolver(validationSchema) as any,
    mode: 'onBlur',
  })

  useEffect(() => {
    if (isOpen) {
      methods.reset({
        claims: initialValues?.claims ?? 0,
        driverYoungerThan24: initialValues?.driverYoungerThan24 ?? false,
        vin: initialValues?.vin ?? ''
      })
    }
  }, [isOpen, initialValues])

  const { setError, formState: { errors } } = methods
  const handleSave = methods.handleSubmit(async (data) => {
    try {
      await onSave(data)
    }
    catch (e: any) {
      if (e?.response?.status === 400 && e?.response?.data) {
        const responseData = e.response.data
        if (responseData[''])
          setError('root', { type: 'manual', message: responseData[''] })
      }
    }
  })

  const vin = methods.watch('vin')
  const [vinInputFinished, setVinInputFinished] = useState(false)

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={!required} closeOnEsc={!required} size={'lg'}>
      <ModalOverlay />
      <ModalContent>
        <FormProvider {...methods}>
          <form onSubmit={handleSave} noValidate>
            <ModalHeader borderBottom={'1px'} borderColor={'gray.100'} as={HStack} gap={4} alignContent={'center'}>
              <BadgeStep>?</BadgeStep>
              <Box>{translate('leasingSimpleWizard.driverInfoDialog.title', 'Doplňující údaje')}</Box>

              {!required && <Button ml={'auto'} onClick={onClose} variant='ghost' size='sm' colorScheme='neutral' p={2}>
                <Icon as={FontAwesomeIcon} icon={faClose} size='xl'></Icon>
              </Button>}
            </ModalHeader>

            <ModalBody py={6} as={Stack} gap={6}>
              <InputFormControl label="VIN"
                name="vin"
                control={methods.control}
                isDisabled={isFirstRegistrationInFuture}
                isRequired={isFirstRegistrationInPast}
                maxLength={17}
                helperText={translate('leasingDriverInfoDialog.vinHelperText', 'Zadejte identifikační číslo vozidla, které naleznete v technickém průkazu')}
                rightElement={
                  <InputRightElement sx={{ minW: '4rem' }}>
                    {vin?.length ?? 0}/{17}
                  </InputRightElement>
                }
                valueTransform={(v) => transformVIN(v as string)}
                onChange={() => {
                  if (vinInputFinished)
                    setVinInputFinished(false)
                }}
                onBlur={() => {
                  if (!vinInputFinished)
                    setVinInputFinished(true)
                }}
              />

              <InvalidVinWarning vin={vin ?? ''} canShow={vinInputFinished} />

              <NumberFormControl
                name="claims"
                label={translate('leasingSimpleWizard.driverInfoDialog.claims', 'Počet nehod klienta')}
                decimalPlaces={0}
                inputRightElement={null}
                showStepper
              />

              {insuranceParams?.client?.type === Selection.Person &&
                <SwitchFormControl
                  name="driverYoungerThan24"
                  label={translate('leasingSimpleWizard.driverInfoDialog.driverYounger24', 'Řidič mladší 24 let')}
                />
              }
            </ModalBody>

            <ModalFooter borderTop={'1px'} borderColor={'gray.100'}>
              <Stack gap={4} w='full'>
                <Button
                  colorScheme="green"
                  type="submit"
                  isLoading={isSaving}
                  ml={'auto'}
                >
                  {translate('save', 'Uložit')}
                </Button>
                {errors.root && <Alert status={'error'}>{errors.root.message}</Alert>}
              </Stack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}
